import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Right-click quick-actions menu for a card: move to column, toggle labels,
// complete/incomplete, archive. Rendered by Board.js at the cursor position;
// closes on any click, Escape, or scroll.
const CardContextMenu = ({ x, y, card, columns, columnSettings, labelOptions, onMove, onToggleLabel, onToggleComplete, onArchive, onClose }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState({ left: x, top: y });

  // Clamp to the viewport once the menu has a measured size.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setPos({
      left: Math.max(8, Math.min(x, window.innerWidth - width - 8)),
      top: Math.max(8, Math.min(y, window.innerHeight - height - 8))
    });
  }, [x, y]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [onClose]);

  const iconFor = (col) => columnSettings?.find(s => s.columnName === col)?.icon || '';
  const cardLabelTexts = new Set((card.labels || []).map(l => l.text));

  const sectionTitle = 'px-3 pt-2 pb-1 text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider';
  const itemClass = 'w-full text-left px-3 py-1.5 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2';

  return (
    <>
      {/* Backdrop: swallow the next click/right-click anywhere */}
      <div
        className="fixed inset-0 z-40"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose(); }}
      />
      <div
        ref={menuRef}
        style={{ left: pos.left, top: pos.top }}
        className="fixed z-50 w-56 max-h-[80vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 py-1"
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="px-3 py-1.5 border-b border-gray-100 dark:border-gray-700 text-xs font-bold text-gray-800 dark:text-gray-100 truncate" title={card.title}>
          {card.title}
        </div>

        <p className={sectionTitle}>Move to</p>
        {columns.filter(c => c !== card.column).map(col => (
          <button key={col} className={itemClass} onClick={() => { onMove(card, col); onClose(); }}>
            <span className="w-4 text-center">{iconFor(col) || '→'}</span>
            <span className="truncate">{col}</span>
          </button>
        ))}

        <p className={sectionTitle}>Labels</p>
        {labelOptions.length === 0 && (
          <p className="px-3 pb-1.5 text-[11px] text-gray-400 dark:text-gray-500 italic">No labels on this board yet</p>
        )}
        {labelOptions.map(label => {
          const active = cardLabelTexts.has(label.text);
          return (
            <button key={label.text} className={itemClass} onClick={() => onToggleLabel(card, label)}>
              <span className="w-3 h-3 rounded-full flex-shrink-0 border border-black/10" style={{ backgroundColor: label.color }} />
              <span className="truncate flex-1">{label.text}</span>
              {active && (
                <svg className="w-3.5 h-3.5 text-primary-blue flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          );
        })}

        <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
        <button className={itemClass} onClick={() => { onToggleComplete(card); onClose(); }}>
          <span className="w-4 text-center">{card.completedAt ? '↩️' : '✅'}</span>
          <span>{card.completedAt ? 'Mark incomplete' : 'Mark complete'}</span>
        </button>
        <button className={`${itemClass} text-red-600 dark:text-red-400`} onClick={() => { onArchive(card); onClose(); }}>
          <span className="w-4 text-center">🗄️</span>
          <span>Archive card</span>
        </button>
      </div>
    </>
  );
};

export default CardContextMenu;
